import React from "react";
import { Link } from "react-router-dom";
import { Check, Tent, Zap, PawPrint, ShowerHead } from "lucide-react";
import SeoLandingLayout from "@/components/SeoLandingLayout";
import { LuxeOverline } from "@/components/LuxeOverline";
import { IMAGES } from "@/lib/site";

const FAQS = [
  {
    q: "How much is a camping pitch per night?",
    a: "Grass tent pitches start from £24 per night and gravel or grass pitches with electric hook-up from £30 per night, based on two guests. Extra guests (up to four per pitch) are £5 each per night.",
  },
  {
    q: "Is electricity included on EHU pitches?",
    a: "Yes — electric hook-up pitches include 15 kWh per day in the price. Heavier use is simply topped up at 33p per kWh with a prepaid card, so there are no surprise bills.",
  },
  {
    q: "Can I choose my exact pitch?",
    a: "Yes — our interactive Pick Your Pitch map lets you browse the park in 3D or aerial view and reserve the exact pitch you want, whether that's a lake-view hardstanding or a quiet corner of the touring field.",
  },
  {
    q: "Are dogs welcome on the campsite?",
    a: "Very much so. Dogs stay free on pitches, and there's a half-mile enclosed dog walk on-site. We just ask that dogs stay on a lead around the park.",
  },
  {
    q: "What facilities are on site?",
    a: "Two modern unisex shower blocks (kept spotless, including an accessible wet room), a dishwashing area with hot water, a drying room, and nine acres of landscaped grounds with two private fishing lakes.",
  },
  {
    q: "What are the arrival times?",
    a: "Pitch check-in is from 1:00 PM (latest arrival 8:00 PM) and check-out is by 11:00 AM. One-night minimum stay — perfect for touring Lincolnshire.",
  },
];

const HIGHLIGHTS = [
  { icon: Zap, title: "Electric included", body: "EHU pitches include 15 kWh/day — then a simple 33p/kWh prepaid top-up." },
  { icon: Tent, title: "Every kind of pitch", body: "Hardstanding lake-view pitches, grass electric pitches and non-electric tent pitches." },
  { icon: PawPrint, title: "Dogs stay free", body: "Half-mile enclosed dog walk on-site, dog-friendly pubs within walking distance." },
  { icon: ShowerHead, title: "Spotless facilities", body: "Two modern shower blocks with an accessible wet room, plus a drying room." },
];

const CampingLincolnshire = () => (
  <SeoLandingLayout
    seo={{
      title: "Camping & Touring in Lincolnshire · Adults-Only Campsite near Boston",
      description:
        "Adults-only campsite in Lincolnshire near Boston & Spalding. Hardstanding electric touring pitches from £30 and tent pitches from £24. Dogs free, spotless facilities, pick your exact pitch.",
      path: "/camping-lincolnshire",
    }}
    overline="Campsite in Lincolnshire"
    titlePre="Adults-only camping & touring in"
    titleEm="Lincolnshire."
    intro="A quiet, family-run campsite in the Lincolnshire fens between Boston and Spalding — hardstanding lake-view pitches with electric hook-up, grass pitches and non-electric tent spots, all a short stroll from two private carp lakes. No crowds, no kids' clubs, just wide fenland sky."
    heroImage={IMAGES.caravanPitches}
    heroAlt="Touring pitches beside Daisy Lake at Walnut Lakes campsite, Lincolnshire"
    faqs={FAQS}
  >
    {/* Highlights */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Why campers choose us</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
        A campsite built for grown-ups
      </h2>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {HIGHLIGHTS.map((h) => (
          <div key={h.title} className="rounded-2xl bg-[#14100F] border border-white/10 p-6">
            <h.icon size={20} className="text-[#E4B2A8]" />
            <h3 className="font-serif text-lg text-[#F5EFE6] mt-4">{h.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-[#D8CDBA]">{h.body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Pitches + pricing */}
    <section className="bg-[#14100F] border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <img
          src={IMAGES.walnutPitches}
          alt="Evening over the camping pitches at Walnut Lakes, an adults-only Lincolnshire campsite"
          className="w-full h-[340px] object-cover rounded-2xl"
          loading="lazy"
        />
        <div>
          <LuxeOverline>Pitches &amp; prices</LuxeOverline>
          <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4">
            Pitches from £24 a night
          </h2>
          <ul className="mt-8 space-y-3">
            {[
              "Hardstanding lake-view pitches with 16A electric hook-up — from £30/night",
              "Grass pitches with electric hook-up — from £30/night",
              "Non-electric grass tent pitches — from £24/night",
              "One-night minimum stay — ideal for touring Lincolnshire",
              "Up to 4 guests per pitch (2 included, +£5 per extra guest/night)",
              "Fishing available to camping guests — £6/day one rod, £10/day two",
            ].map((i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[#D8CDBA]">
                <Check size={16} className="text-[#E4B2A8] mt-0.5 flex-shrink-0" /> {i}
              </li>
            ))}
          </ul>
          <Link to="/pick-your-pitch" className="mt-8 inline-block btn-primary font-brand tracking-[0.14em]" data-testid="seo-camping-pick-pitch">
            Pick your exact pitch
          </Link>
        </div>
      </div>
    </section>

    {/* Location context */}
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
      <LuxeOverline>Touring base</LuxeOverline>
      <h2 className="font-serif text-3xl md:text-4xl text-[#F5EFE6] mt-4 max-w-2xl">
        The perfect base for exploring Lincolnshire
      </h2>
      <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-[#D8CDBA]">
        Just off the A16/A17 roundabout near Sutterton, the park is minutes from Boston and
        Spalding, with bus links from the village a 15-minute walk away. RSPB Frampton Marsh,
        Moulton Marsh Nature Reserve and the historic Boston Stump are all within a 15-minute
        drive — and the Lincolnshire coast makes an easy day out.
      </p>
    </section>
  </SeoLandingLayout>
);

export default CampingLincolnshire;
